import { ensurePolyLine2HasVerticalEnds, profile2OrientCopy } from "building-common/profile2Utils";
import { DIST_EPSILON } from "geom/core";
import { offsetPolyLine2SSkFallback } from "geom/offset";
import { polyLine2Reverse, polyLine2Close, polyLine2SetCW, polyLine2SetCCW, polyLine2Simplify } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2Dist } from "geom/vec2";

interface HorizontalOffsetShapes {
  casingOutline: PolyLine2;
  arcCurve: PolyLine2 | undefined;
  arcCurveSimple: PolyLine2 | undefined;
}

export function offsetCasingShapes(horizontalOffset: number, shapes: HorizontalOffsetShapes): HorizontalOffsetShapes {
  if (horizontalOffset === 0) {
    return shapes;
  }

  let { arcCurve, arcCurveSimple, casingOutline } = shapes;

  casingOutline = profile2OrientCopy(polyLine2SetCW(casingOutline));

  // un-close the casing outline so that the bottom edge is not offset
  const withoutBottom = polyLine2New(casingOutline.slice(2));

  const firstPtCasing = [withoutBottom[0], withoutBottom[1]];
  const lastPtCasing = [withoutBottom[casingOutline.length - 2], withoutBottom[casingOutline.length - 1]];

  arcCurve = arcCurve && polyLine2SetCW(arcCurve);
  arcCurveSimple = arcCurveSimple && polyLine2SetCW(arcCurveSimple);

  if (arcCurve) {
    const firstPtArc = [arcCurve[0], arcCurve[1]];
    const lastPtArc = [arcCurve[arcCurve.length - 2], arcCurve[arcCurve.length - 1]];

    // add the full side of the opening to the arc curve so that offsetting is clean
    const arcCurveSides = polyLine2Simplify(
      polyLine2New([
        withoutBottom[0],
        withoutBottom[1],
        ...arcCurve,
        withoutBottom[withoutBottom.length - 2],
        withoutBottom[withoutBottom.length - 1],
      ]),
    );

    let arcCurveSidesOffset = offsetPolyLine2SSkFallback(arcCurveSides, horizontalOffset);
    if (arcCurveSidesOffset.length > 0) {
      polyLine2SetCW(arcCurveSidesOffset);
      if (
        vec2Dist(firstPtArc[0], firstPtArc[1], arcCurveSidesOffset[0], arcCurveSidesOffset[1]) >
        vec2Dist(lastPtArc[0], lastPtArc[1], arcCurveSidesOffset[0], arcCurveSidesOffset[1]) + DIST_EPSILON
      ) {
        arcCurveSidesOffset = polyLine2Reverse(arcCurveSidesOffset);
      }
      arcCurve = arcCurveSidesOffset.slice(2, -2);
      arcCurve = ensurePolyLine2HasVerticalEnds(arcCurve);
    }
  }

  if (arcCurveSimple) {
    const firstPtArcSimple = [arcCurveSimple[0], arcCurveSimple[1]];
    const lastPtArcSimple = [arcCurveSimple[arcCurveSimple.length - 2], arcCurveSimple[arcCurveSimple.length - 1]];

    // arc curve simple does not need the sides to preserve corner features
    let arcSimpleOffset = offsetPolyLine2SSkFallback(arcCurveSimple, horizontalOffset);
    if (arcSimpleOffset.length > 0) {
      polyLine2SetCW(arcSimpleOffset);
      if (
        vec2Dist(firstPtArcSimple[0], firstPtArcSimple[1], arcSimpleOffset[0], arcSimpleOffset[1]) >
        vec2Dist(lastPtArcSimple[0], lastPtArcSimple[1], arcSimpleOffset[0], arcSimpleOffset[1])
      ) {
        arcSimpleOffset = polyLine2Reverse(arcSimpleOffset);
      }
      arcCurveSimple = arcSimpleOffset;
    }
  }

  let withoutBottomOffset = offsetPolyLine2SSkFallback(withoutBottom, horizontalOffset);
  if (withoutBottomOffset.length > 0) {
    polyLine2SetCCW(withoutBottomOffset);
    // @NOTE(Andrew): a naive check to try to determine if the StraightSkeleton offset reversed the shape
    if (
      vec2Dist(firstPtCasing[0], firstPtCasing[1], withoutBottomOffset[0], withoutBottomOffset[1]) >
      vec2Dist(lastPtCasing[0], lastPtCasing[1], withoutBottomOffset[0], withoutBottomOffset[1]) + DIST_EPSILON
    ) {
      withoutBottomOffset = polyLine2Reverse(withoutBottomOffset);
    }
    // re-add the bottom edge, ensuring its index is preserved
    casingOutline = profile2OrientCopy(polyLine2Close(withoutBottomOffset));
  }

  arcCurve = arcCurve && polyLine2Reverse(arcCurve);
  arcCurveSimple = arcCurveSimple && polyLine2Reverse(arcCurveSimple);

  return {
    casingOutline,
    arcCurve,
    arcCurveSimple,
  };
}
